/**
 * Advanced Filters Component
 * Provides search bar and filtering options for the course list
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { coursesService, CourseFilters, SearchCoursesParams } from '../../services/coursesService';
import { TRANSLATION_LANGUAGES } from '../creator-workflow/resource-modal/useResourceModalFlow';
import SearchBar, { SearchBarRef } from '../common/SearchBar';
import { CEFRGuideModal } from '../cefr';
import './AdvancedFilters.css';

interface AdvancedFiltersProps {
  currentFilters: SearchCoursesParams;
  onFilterChange: (filters: SearchCoursesParams) => void;
  isOpen: boolean;
  onToggle: () => void;
  searchPlaceholder?: string;
}

const SECONDS_PER_MINUTE = 60;

const DURATION_OPTIONS = [
  { labelKey: 'filters.durationAll' as const, min: undefined, max: undefined },
  { labelKey: 'filters.durationLt1' as const, min: undefined, max: 1 * SECONDS_PER_MINUTE },
  { labelKey: 'filters.duration1to3' as const, min: 1 * SECONDS_PER_MINUTE, max: 3 * SECONDS_PER_MINUTE },
  { labelKey: 'filters.duration3to5' as const, min: 3 * SECONDS_PER_MINUTE, max: 5 * SECONDS_PER_MINUTE },
  { labelKey: 'filters.durationGt5' as const, min: 5 * SECONDS_PER_MINUTE, max: undefined },
];

const RATING_OPTIONS = [
  { labelKey: 'filters.ratingAll' as const, value: undefined },
  { labelKey: 'filters.rating4Plus' as const, value: 4 },
  { labelKey: 'filters.rating3Plus' as const, value: 3 },
  { labelKey: 'filters.rating2Plus' as const, value: 2 },
];

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

const SORT_OPTIONS = [
  { labelKey: 'filters.sortNewest' as const, value: 'newest' },
  { labelKey: 'filters.sortPopular' as const, value: 'popular' },
  { labelKey: 'filters.sortRating' as const, value: 'rating' },
  { labelKey: 'filters.sortDuration' as const, value: 'duration' },
];

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  currentFilters,
  onFilterChange,
  isOpen,
  onToggle,
  searchPlaceholder,
}) => {
  const { t, i18n } = useTranslation('course');
  const [filters, setFilters] = useState<CourseFilters | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDurationIdx, setSelectedDurationIdx] = useState(0);
  const [searchQuery, setSearchQuery] = useState(currentFilters.q || '');
  const [showCefrGuide, setShowCefrGuide] = useState(false);
  const searchBarRef = useRef<SearchBarRef>(null);

  const resolvedPlaceholder = searchPlaceholder || t('filters.searchPlaceholder');

  useEffect(() => {
    loadFilters();
  }, []);

  // Sync search query with currentFilters.q
  useEffect(() => {
    setSearchQuery(currentFilters.q || '');
  }, [currentFilters.q]);

  // Sync duration selection with currentFilters
  useEffect(() => {
    const idx = DURATION_OPTIONS.findIndex(
      opt => opt.min === currentFilters.minDuration && opt.max === currentFilters.maxDuration
    );
    if (idx >= 0) {
      setSelectedDurationIdx(idx);
    }
  }, [currentFilters.minDuration, currentFilters.maxDuration]);

  const loadFilters = async () => {
    try {
      const data = await coursesService.getFilters();
      setFilters(data);
    } catch (err) {
      console.error('Failed to load filters:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCefrLevelChange = (level: string | undefined) => {
    onFilterChange({
      ...currentFilters,
      cefrLevel: level as SearchCoursesParams['cefrLevel'],
    });
  };

  const handleCreatorChange = (creatorId: string | undefined) => {
    onFilterChange({
      ...currentFilters,
      creatorId,
    });
  };

  const handleRatingChange = (minRating: number | undefined) => {
    onFilterChange({
      ...currentFilters,
      minRating,
    });
  };

  const handleDurationChange = (idx: number) => {
    setSelectedDurationIdx(idx);
    const opt = DURATION_OPTIONS[idx];
    onFilterChange({
      ...currentFilters,
      minDuration: opt.min,
      maxDuration: opt.max,
    });
  };

  const handleSortChange = (sortBy: string) => {
    onFilterChange({
      ...currentFilters,
      sortBy: sortBy as SearchCoursesParams['sortBy'],
    });
  };

  const handleLanguageChange = (language: string | undefined) => {
    onFilterChange({
      ...currentFilters,
      translationLanguage: language,
    });
  };

  const handleTopicChange = (topic: string | undefined) => {
    onFilterChange({
      ...currentFilters,
      topic,
    });
  };

  const handleAccentChange = (accent: string | undefined) => {
    onFilterChange({
      ...currentFilters,
      accent,
    });
  };

  const handleContentTypeChange = (contentType: SearchCoursesParams['contentType'] | undefined) => {
    onFilterChange({
      ...currentFilters,
      contentType,
    });
  };

  const handleVisibilityChange = (visibility: 'public' | 'private' | undefined) => {
    onFilterChange({
      ...currentFilters,
      visibility,
    });
  };

  const handleSearchSubmit = (query: string) => {
    onFilterChange({
      ...currentFilters,
      q: query || undefined,
    });
  };

  const handleClearSearch = () => {
    onFilterChange({
      ...currentFilters,
      q: undefined,
    });
  };

  const handleClearAll = () => {
    setSelectedDurationIdx(0);
    setSearchQuery('');
    onFilterChange({});
  };

  const hasActiveFilters = !!(
    currentFilters.difficulty ||
    currentFilters.cefrLevel ||
    currentFilters.creatorId ||
    currentFilters.translationLanguage ||
    currentFilters.topic ||
    currentFilters.accent ||
    currentFilters.contentType ||
    currentFilters.visibility ||
    currentFilters.minRating ||
    currentFilters.minDuration !== undefined ||
    currentFilters.maxDuration !== undefined ||
    (currentFilters.sortBy && currentFilters.sortBy !== 'newest')
  );

  if (!isOpen) {
    return (
      <div className="filters-toolbar">
        {/* Search Bar */}
        <SearchBar
          ref={searchBarRef}
          value={searchQuery}
          onChange={setSearchQuery}
          onSubmit={handleSearchSubmit}
          onClear={handleClearSearch}
          placeholder={resolvedPlaceholder}
          showSubmitButton
        />

        {/* Filter Toggle */}
        <button className="btn-toggle-filters" onClick={onToggle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          {t('filters.advancedFilters')}
          {hasActiveFilters && <span className="active-filter-badge" />}
        </button>
        {(hasActiveFilters || currentFilters.q) && (
          <button className="btn-clear-inline" onClick={handleClearAll}>
            {t('filters.clearAll')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="advanced-filters">
      {/* Search Bar in expanded mode */}
      <div className="filters-search-section">
        <SearchBar
          ref={searchBarRef}
          value={searchQuery}
          onChange={setSearchQuery}
          onSubmit={handleSearchSubmit}
          onClear={handleClearSearch}
          placeholder={resolvedPlaceholder}
          showSubmitButton
          className="search-bar--expanded"
        />
      </div>

      <div className="filters-header">
        <h3>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          {t('filters.advancedFilters')}
        </h3>
        <div className="filters-header-actions">
          {(hasActiveFilters || currentFilters.q) && (
            <button className="btn-clear" onClick={handleClearAll}>
              {t('filters.clearAll')}
            </button>
          )}
          <button className="btn-collapse" onClick={onToggle} aria-label={t('filters.collapseFilters')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
        </div>
      </div>

      <div className="filters-content">
        {/* CEFR Level */}
        <div className="filter-group">
          <div className="filter-group-label">
            <label id="cefr-label">{t('filters.cefrLevel')}</label>
            <button
              type="button"
              className="filter-info-btn"
              onClick={() => setShowCefrGuide(true)}
              aria-label={t('filters.cefrGuideAria', { defaultValue: 'About CEFR levels' })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          </div>
          <div className="filter-chips" role="group" aria-labelledby="cefr-label">
            <button
              className={`filter-chip ${!currentFilters.cefrLevel ? 'active' : ''}`}
              onClick={() => handleCefrLevelChange(undefined)}
              aria-pressed={!currentFilters.cefrLevel}
            >
              {t('filters.all')}
            </button>
            {CEFR_LEVELS.map((level) => {
              const count = filters?.cefrLevels?.find(c => c.level === level)?.count || 0;
              return (
                <button
                  key={level}
                  className={`filter-chip ${currentFilters.cefrLevel === level ? 'active' : ''}`}
                  onClick={() => handleCefrLevelChange(level)}
                  aria-pressed={currentFilters.cefrLevel === level}
                >
                  {level}
                  {count > 0 && <span className="chip-count">({count})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Topic */}
        {filters?.topics && filters.topics.length > 0 && (
          <div className="filter-group">
            <label id="topic-label">{t('filters.topic')}</label>
            <div className="filter-chips" role="group" aria-labelledby="topic-label">
              <button
                className={`filter-chip ${!currentFilters.topic ? 'active' : ''}`}
                onClick={() => handleTopicChange(undefined)}
                aria-pressed={!currentFilters.topic}
              >
                {t('filters.allTopics')}
              </button>
              {filters.topics.map((topic) => (
                <button
                  key={topic.code}
                  className={`filter-chip ${currentFilters.topic === topic.code ? 'active' : ''}`}
                  onClick={() => handleTopicChange(topic.code)}
                  aria-pressed={currentFilters.topic === topic.code}
                >
                  {topic.icon && <span className="topic-icon" aria-hidden="true">{topic.icon}</span>}
                  {topic.labels[i18n.language] || topic.labels['en'] || topic.code}
                  {topic.courseCount > 0 && <span className="chip-count">({topic.courseCount})</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Translation Language */}
        <div className="filter-group">
          <label id="language-label">{t('filters.translationLanguage')}</label>
          <div className="filter-chips" role="group" aria-labelledby="language-label">
            <button
              className={`filter-chip ${!currentFilters.translationLanguage ? 'active' : ''}`}
              onClick={() => handleLanguageChange(undefined)}
              aria-pressed={!currentFilters.translationLanguage}
            >
              {t('filters.allLanguages')}
            </button>
            {TRANSLATION_LANGUAGES.map((lang) => {
              const count = filters?.translationLanguages?.find(l => l.language === lang.code)?.count || 0;
              return (
                <button
                  key={lang.code}
                  className={`filter-chip ${currentFilters.translationLanguage === lang.code ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                  aria-pressed={currentFilters.translationLanguage === lang.code}
                >
                  {lang.flag} {lang.name}
                  {count > 0 && <span className="chip-count">({count})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Accent */}
        {filters?.accents && filters.accents.length > 0 && (
          <div className="filter-group">
            <label id="accent-label">{t('filters.accent')}</label>
            <div className="filter-chips" role="group" aria-labelledby="accent-label">
              <button
                className={`filter-chip ${!currentFilters.accent ? 'active' : ''}`}
                onClick={() => handleAccentChange(undefined)}
                aria-pressed={!currentFilters.accent}
              >
                {t('filters.allAccents')}
              </button>
              {filters.accents.map((a) => (
                <button
                  key={a.accent}
                  className={`filter-chip ${currentFilters.accent === a.accent ? 'active' : ''}`}
                  onClick={() => handleAccentChange(a.accent)}
                  aria-pressed={currentFilters.accent === a.accent}
                >
                  {t(`filters.accents.${a.accent}`, a.accent)}
                  <span className="chip-count">({a.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content Type (dialogue / article / news / speech / advertisement / story) */}
        {filters?.contentTypes && filters.contentTypes.length > 0 && (
          <div className="filter-group">
            <label id="content-type-label">{t('filters.contentType')}</label>
            <div className="filter-chips" role="group" aria-labelledby="content-type-label">
              <button
                className={`filter-chip ${!currentFilters.contentType ? 'active' : ''}`}
                onClick={() => handleContentTypeChange(undefined)}
                aria-pressed={!currentFilters.contentType}
              >
                {t('filters.all')}
              </button>
              {filters.contentTypes.map((ct) => (
                <button
                  key={ct.contentType}
                  className={`filter-chip ${currentFilters.contentType === ct.contentType ? 'active' : ''}`}
                  onClick={() => handleContentTypeChange(ct.contentType as SearchCoursesParams['contentType'])}
                  aria-pressed={currentFilters.contentType === ct.contentType}
                >
                  {t(`filters.contentTypes.${ct.contentType}`, { defaultValue: ct.contentType })}
                  <span className="chip-count">({ct.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Visibility (Public / Private) */}
        <div className="filter-group">
          <label id="visibility-label">{t('filters.visibility')}</label>
          <div className="filter-chips" role="group" aria-labelledby="visibility-label">
            <button
              className={`filter-chip ${!currentFilters.visibility ? 'active' : ''}`}
              onClick={() => handleVisibilityChange(undefined)}
              aria-pressed={!currentFilters.visibility}
            >
              {t('filters.all')}
            </button>
            <button
              className={`filter-chip ${currentFilters.visibility === 'public' ? 'active' : ''}`}
              onClick={() => handleVisibilityChange('public')}
              aria-pressed={currentFilters.visibility === 'public'}
            >
              {t('filters.visibilityPublic')}
            </button>
            <button
              className={`filter-chip ${currentFilters.visibility === 'private' ? 'active' : ''}`}
              onClick={() => handleVisibilityChange('private')}
              aria-pressed={currentFilters.visibility === 'private'}
            >
              {t('filters.visibilityPrivate')}
            </button>
          </div>
        </div>

        {/* Creator */}
        <div className="filter-group">
          <label htmlFor="creator-select">{t('filters.creator')}</label>
          <select
            id="creator-select"
            value={currentFilters.creatorId || ''}
            onChange={(e) => handleCreatorChange(e.target.value || undefined)}
            className="filter-select"
          >
            <option value="">{t('filters.allCreators')}</option>
            {!loading && filters?.creators.map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullName || c.username} ({c.courseCount})
              </option>
            ))}
          </select>
        </div>

        {/* Rating */}
        <div className="filter-group">
          <label id="rating-label">{t('filters.rating')}</label>
          <div className="filter-chips" role="group" aria-labelledby="rating-label">
            {RATING_OPTIONS.map((opt) => (
              <button
                key={opt.labelKey}
                className={`filter-chip ${currentFilters.minRating === opt.value ? 'active' : ''}`}
                onClick={() => handleRatingChange(opt.value)}
                aria-pressed={currentFilters.minRating === opt.value}
              >
                {opt.value && <span className="star-icon" aria-hidden="true">⭐</span>}
                {t(opt.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Duration */}
        <div className="filter-group">
          <label id="duration-label">{t('filters.duration')}</label>
          <div className="filter-chips" role="group" aria-labelledby="duration-label">
            {DURATION_OPTIONS.map((opt, idx) => (
              <button
                key={opt.labelKey}
                className={`filter-chip ${selectedDurationIdx === idx ? 'active' : ''}`}
                onClick={() => handleDurationChange(idx)}
                aria-pressed={selectedDurationIdx === idx}
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Sort */}
        <div className="filter-group">
          <label id="sort-label">{t('filters.sortBy')}</label>
          <div className="filter-chips" role="group" aria-labelledby="sort-label">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`filter-chip ${(currentFilters.sortBy || 'newest') === opt.value ? 'active' : ''}`}
                onClick={() => handleSortChange(opt.value)}
                aria-pressed={(currentFilters.sortBy || 'newest') === opt.value}
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <CEFRGuideModal isOpen={showCefrGuide} onClose={() => setShowCefrGuide(false)} />
    </div>
  );
};

export default AdvancedFilters;
